/**
 * Clears persisted state that describes work in progress.
 *
 * @author Milton Filho
 */
import { FetchStatus } from 'types/types';

/**
 * Values that only make sense while something is in flight, and what each
 *   should read as once it is not.
 *
 * `Fetching` was the first. `syncing` is the same shape with a different
 *   spelling, and being a different *value* is exactly how it escaped for so
 *   long: it is `offlineSync.syncStatus` during a queue drain and only
 *   `SYNC_COMPLETE` returns it to `idle`. An app killed mid-drain — iOS
 *   evicting a backgrounded PWA is enough — persists `syncing`, and
 *   `syncOnReconnect$` refuses to start a drain while it reads that. Every
 *   reconnect and every launch afterwards did nothing, so queued arrivals and
 *   bookings stopped syncing permanently, with no toast and nothing for the
 *   therapist to notice.
 *
 * A `Map` rather than an object literal, because the lookup key is a value out
 *   of persisted state: an object reaches its prototype, so a slice holding the
 *   string `constructor` or `toString` would find a function and be overwritten
 *   with it.
 */
const TRANSIENT_VALUES = new Map<string, string>([
  [FetchStatus.Fetching, FetchStatus.None],
  ['syncing', 'idle'],
]);

/**
 * Resets any in-progress marker found anywhere in a rehydrated state tree.
 *
 * Persisted, such a value never resolves and permanently disables whatever
 *   waits on it — the tab closed mid-request, or the app evicted mid-sync.
 *   Mutates the object in place, because it runs on the parsed state before the
 *   store exists.
 *
 * Lives in its own module so it can be tested against shapes the store cannot
 *   produce: the slice reducers drop keys they do not declare, so an arbitrary
 *   field is unreachable through rehydration.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const resetTransientValues = (obj: any): void => {
  if (!obj || typeof obj !== 'object') return;

  for (const key of Object.keys(obj)) {
    const value = obj[key];

    // Matched on the value, not on the key name. Keying on `fetchStatus`
    //   silently exempted every differently-named status field: `files` gained
    //   a `requestFormStatus`, it was never reset, and a signing flow that did
    //   not reach a terminal action left the Sign button disabled *across
    //   reloads* — exactly the failure this function exists to prevent.
    //
    // Matching a value as ordinary as `syncing` could in principle reset an
    //   unrelated field that legitimately persists it. Nothing in the state does
    //   today, and the two mistakes are not comparable: a marker wrongly read as
    //   finished recovers on the next action, while one wrongly read as
    //   in-flight never recovers at all.
    const resetTo =
      typeof value === 'string' ? TRANSIENT_VALUES.get(value) : undefined;

    if (resetTo !== undefined) {
      obj[key] = resetTo;
    } else if (typeof value === 'object' && value !== null) {
      // Arrays included: a slice holding objects would otherwise be skipped.
      resetTransientValues(value);
    }
  }
};
